import React, { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Users,
  Briefcase,
  TrendingUp,
  DollarSign,
  Calendar,
  Building,
  CheckCircle,
  XCircle,
  Clock,
  Edit,
  Trash2,
  Eye,
  AlertCircle,
} from 'lucide-react';
import api from '../lib/api';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Modal from '../components/Modal';
import { Input, Select } from '../components/Form';

interface Employee {
  id: string;
  person: {
    id: string;
    name: string;
    document: string;
    email: string;
    phone: string;
  };
  department?: {
    id: string;
    name: string;
  };
  contractType: string;
  employmentStatus: string;
  commissionType: string;
  jobTitle: string;
  admissionDate: string;
  baseSalary: number;
  commissionRate: number;
}

interface Department {
  id: string;
  name: string;
}

interface Person {
  id: string;
  name: string;
  document: string;
  email: string;
}

const formatCurrencyBRL = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number.isFinite(Number(value)) ? Number(value) : 0);

const parseCurrencyBRL = (value: string): number => {
  if (!value) return 0;
  const cleaned = value
    .replace(/\s/g, '')
    .replace(/[R$]/g, '')
    .replace(/\./g, '')
    .replace(/,/g, '.')
    .replace(/[^0-9.-]/g, '');
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

export default function EmployeesPage() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [filterDepartment, setFilterDepartment] = useState<string>('ALL');
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [formData, setFormData] = useState({
    personId: '',
    departmentId: '',
    contractType: 'CLT',
    commissionType: 'FIXED_ONLY',
    jobTitle: '',
    admissionDate: new Date().toISOString().split('T')[0],
    baseSalary: 0,
    commissionRate: 0,
    guaranteedMinimum: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [employeesRes, departmentsRes, peopleRes] = await Promise.all([
        api.get('/employees'),
        api.get('/departments'),
        api.get('/people'),
      ]);
      setEmployees(employeesRes.data.data || []);
      setDepartments(departmentsRes.data.data || []);
      setPeople(peopleRes.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      alert('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!formData.personId) {
        alert('Selecione a pessoa para vincular o funcionário.');
        return;
      }

      if (!formData.departmentId) {
        alert('Selecione o departamento do funcionário.');
        return;
      }

      const payload = {
        ...formData,
        baseSalary: Number(formData.baseSalary) || 0,
        commissionRate: Number(formData.commissionRate) || 0,
        guaranteedMinimum: Number(formData.guaranteedMinimum) || 0,
      };

      if (editingEmployee) {
        await api.put(`/employees/${editingEmployee.id}`, payload);
      } else {
        await api.post('/employees', payload);
      }
      setShowModal(false);
      resetForm();
      await loadData();
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      alert(error.response?.data?.message || 'Erro ao salvar funcionário');
    }
  };

  const handleEdit = (employee: Employee) => {
    setEditingEmployee(employee);
    setFormData({
      personId: employee.person.id,
      departmentId: employee.department?.id || '',
      contractType: employee.contractType,
      commissionType: employee.commissionType,
      jobTitle: employee.jobTitle,
      admissionDate: employee.admissionDate.split('T')[0],
      baseSalary: Number(employee.baseSalary || 0),
      commissionRate: Number(employee.commissionRate || 0),
      guaranteedMinimum: 0,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Deseja realmente excluir este funcionário?')) return;
    try {
      await api.delete(`/employees/${id}`);
      await loadData();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert('Erro ao excluir funcionário');
    }
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setFormData({
      personId: '',
      departmentId: '',
      contractType: 'CLT',
      commissionType: 'FIXED_ONLY',
      jobTitle: '',
      admissionDate: new Date().toISOString().split('T')[0],
      baseSalary: 0,
      commissionRate: 0,
      guaranteedMinimum: 0,
    });
  };

  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch = emp.person.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'ALL' || emp.employmentStatus === filterStatus;
    const matchesDept = filterDepartment === 'ALL' || emp.department?.id === filterDepartment;
    return matchesSearch && matchesStatus && matchesDept;
  });

  const statusColors: Record<string, string> = {
    ACTIVE: 'bg-green-100 text-green-800',
    ON_LEAVE: 'bg-yellow-100 text-yellow-800',
    TERMINATED: 'bg-red-100 text-red-800',
    SUSPENDED: 'bg-gray-100 text-gray-800',
  };

  const statusLabels: Record<string, string> = {
    ACTIVE: 'Ativo',
    ON_LEAVE: 'Afastado',
    TERMINATED: 'Desligado',
    SUSPENDED: 'Suspenso',
  };

  const contractLabels: Record<string, string> = {
    CLT: 'CLT',
    PJ: 'PJ',
    AUTONOMOUS: 'Autônomo',
    INTERN: 'Estagiário',
  };

  const commissionLabels: Record<string, string> = {
    FIXED_ONLY: 'Fixo',
    PURE_COMMISSION: 'Comissão Pura',
    MIXED: 'Fixo + Comissão',
  };

  const stats = {
    total: employees.length,
    active: employees.filter((e) => e.employmentStatus === 'ACTIVE').length,
    onLeave: employees.filter((e) => e.employmentStatus === 'ON_LEAVE').length,
    totalSalary: employees
      .filter((e) => e.employmentStatus === 'ACTIVE')
      .reduce((sum, e) => sum + Number(e.baseSalary), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Users className="h-8 w-8" />
          Gestão de Funcionários
        </h1>
        <p className="text-gray-600 mt-2">Gerencie funcionários, comissões e folha de pagamento</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total de Funcionários</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <Users className="h-10 w-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Ativos</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.active}</p>
            </div>
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Afastados</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.onLeave}</p>
            </div>
            <AlertCircle className="h-10 w-10 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Folha Mensal</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">
                {new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(stats.totalSalary)}
              </p>
            </div>
            <DollarSign className="h-10 w-10 text-purple-500" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Buscar por nome..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">Todos os Status</option>
                <option value="ACTIVE">Ativos</option>
                <option value="ON_LEAVE">Afastados</option>
                <option value="TERMINATED">Desligados</option>
                <option value="SUSPENDED">Suspensos</option>
              </select>

              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">Todos os Departamentos</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <Plus className="h-5 w-5" />
              Novo Funcionário
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Funcionário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cargo / Departamento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contrato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Comissão
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Salário Base
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Admissão
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEmployees.map((employee) => (
                <tr key={employee.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="font-medium text-gray-900">{employee.person.name}</div>
                      <div className="text-sm text-gray-500">{employee.person.document}</div>
                      <div className="text-sm text-gray-500">{employee.person.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{employee.jobTitle}</div>
                    <div className="text-sm text-gray-500 flex items-center gap-1">
                      <Building className="h-4 w-4" />
                      {employee.department?.name || 'Sem departamento'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {contractLabels[employee.contractType]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm text-gray-900">{commissionLabels[employee.commissionType]}</div>
                      {employee.commissionRate > 0 && (
                        <div className="text-sm text-gray-500">{employee.commissionRate}%</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      }).format(Number(employee.baseSalary))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      {format(parseISO(employee.admissionDate), 'dd/MM/yyyy', { locale: ptBR })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColors[employee.employmentStatus]}`}>
                      {statusLabels[employee.employmentStatus]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(employee)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Editar"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(employee.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Excluir"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredEmployees.length === 0 && (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">Nenhum funcionário encontrado</p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            resetForm();
          }}
          title={editingEmployee ? 'Editar Funcionário' : 'Novo Funcionário'}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pessoa</label>
              <select
                value={formData.personId}
                onChange={(e) => setFormData({ ...formData, personId: e.target.value })}
                required
                disabled={!!editingEmployee}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecione uma pessoa</option>
                {people.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name} - {person.document}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Departamento</label>
              <select
                value={formData.departmentId}
                onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecione o departamento</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Contrato</label>
                <select
                  value={formData.contractType}
                  onChange={(e) => setFormData({ ...formData, contractType: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="CLT">CLT</option>
                  <option value="PJ">PJ</option>
                  <option value="AUTONOMOUS">Autônomo</option>
                  <option value="INTERN">Estagiário</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Comissão</label>
                <select
                  value={formData.commissionType}
                  onChange={(e) => setFormData({ ...formData, commissionType: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="FIXED_ONLY">Fixo</option>
                  <option value="PURE_COMMISSION">Comissão Pura</option>
                  <option value="MIXED">Fixo + Comissão</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cargo</label>
              <input
                type="text"
                value={formData.jobTitle}
                onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                required
                placeholder="Ex: Corretor, Gerente, Auxiliar"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salário Base</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatCurrencyBRL(formData.baseSalary)}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      baseSalary: parseCurrencyBRL(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Taxa Comissão (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.commissionRate}
                  onChange={(e) => setFormData({ ...formData, commissionRate: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de Admissão</label>
                <input
                  type="date"
                  value={formData.admissionDate}
                  onChange={(e) => setFormData({ ...formData, admissionDate: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {formData.commissionType === 'PURE_COMMISSION' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Garantia Mínima (Salário Mínimo)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatCurrencyBRL(formData.guaranteedMinimum)}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      guaranteedMinimum: parseCurrencyBRL(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingEmployee ? 'Atualizar' : 'Cadastrar'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
